/**
 * FillTailorSurface — the REAL fill campaign's response surface building up in the Tailor
 * 2D/3D/highD framework (FXC-9009).
 *
 * Replaces the bespoke FillProjection tile: instead of a hand-rolled scatter, this fetches the
 * campaign's real composite /surface (the confidence-weighted fused grid), adapts it onto the
 * adaptive-sampling framework's types, and renders the SAME components that framework uses — the
 * 3D TailorSurfaceBuild (which re-fits + re-polls as points land) and a 2D estimate heatmap of the
 * chosen slice. Pick any two campaign axes for the 2D slice; every other range axis pins (highD).
 */
import { useMemo } from "react";

import type { CampaignDetail } from "../../../api/highliftCampaign";
import type { HighliftPalette } from "../foil/useHighliftTheme";
import { HeatmapPanel } from "../../adaptive-sampling/primitives";
import { type StudyEvidenceMode } from "../../adaptive-sampling/surfaces/useSliceState";
import { TailorSurfaceBuild } from "../../thread-v5/ThreadV5TailorSurfaceBuild";
import { projectionAxes } from "./FillProjection";
import { compositeToSurfaceResponse, campaignToAxisSpecs, campaignToTailorState } from "./tailorSurfaceAdapter";
import { useDebouncedStudySlice, useStudySurface } from "./useStudySurface";
import {
  fitTailorSliceModel,
  summarizeTailorEvidence,
  tailorValueForMode,
  type TailorAxisInput,
  type TailorEvidenceMode,
} from "../../thread-v5/adbTailorSlice";

import { FILL_AXES } from "./campaignMath";

const MONO = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";
const AXIS_LABELS = new Map(FILL_AXES.map((a) => [a.canonical, a.label]));
const SURFACE_POLL_MS = 4000;

export function FillTailorSurface({
  campaign,
  projectId,
  branch,
  palette,
}: {
  campaign: CampaignDetail;
  projectId: string;
  branch: string;
  palette: HighliftPalette;
}) {
  const axes = useMemo(() => projectionAxes(campaign.config?.axes ?? []), [campaign]);
  const ranged = axes.filter((a) => a.ranged);

  // Study Cursor (FXC-9009 two-rooms): the axis choice, pins and evidence mode
  // are the SHARED project+branch cursor — this panel is a stateless subscriber,
  // so it can never desync from any other surface on the same cursor. The
  // campaign's declared box supplies the axis metadata that initialises it.
  // Adversarial-review fix: the fetch keys on the SAME debounced spec +
  // canonical study-surface query every other lane uses — one request per
  // (quantity, slice) shared across the board, no private cache namespace,
  // no per-slider-tick refetch.
  const axisSpecs = useMemo(() => campaignToAxisSpecs(campaign.config?.axes ?? []), [campaign]);
  const { cursor, patchCursor, spec } = useDebouncedStudySlice(projectId, branch, axisSpecs);
  const xAxis = cursor.xInput || undefined;
  const yAxis = cursor.yInput || undefined;
  const pins = cursor.pinned;
  const setXY = (nextX: string, nextY: string) => patchCursor({ xInput: nextX, yInput: nextY });
  const setPin = (axis: string, value: number) => patchCursor({ pinned: { [axis]: value } });

  // Only RANGED axes are pickable for x/y: a held axis is a single point, so a surface along it
  // is degenerate — offering it just makes the interface harder for no benefit.
  const pickable = ranged.length >= 2 ? ranged : axes;
  const x = pickable.find((a) => a.canonical === xAxis) ?? pickable[0];
  const y = pickable.find((a) => a.canonical === yAxis && a.canonical !== x?.canonical)
    ?? pickable.find((a) => a.canonical !== x?.canonical);
  const running = campaign.state?.status === "running" || campaign.state?.status === "paused";

  // The high-D axes the 2D slice hides: those the composite surface pins (fixed for the fetch)
  // and the framework's pin sliders adjust.
  const pinAxes = axes.filter((a) => a.canonical !== x?.canonical && a.canonical !== y?.canonical);
  const sliceOverrides = useMemo(() => Object.fromEntries(
    pinAxes.map((a) => [a.canonical, Math.min(a.max, Math.max(a.min, pins[a.canonical] ?? (a.min + a.max) / 2))]),
  ), [pinAxes, pins]);

  // The REAL composite surface for the chosen slice — re-fetched as the campaign lands points so
  // the surface visibly builds up (the same "server re-fits, client re-polls" the framework uses).
  // Shared canonical query: identical key/grid to the 2D board's CL lane, so a
  // 2-up/quad shows ONE in-flight fetch, not near-duplicates.
  const surfaceQuery = useStudySurface(projectId, branch, spec, "cl", {
    enabled: !!x && !!y,
    pollMs: running ? SURFACE_POLL_MS : false,
  });

  const adapted = useMemo(
    () => (surfaceQuery.data ? compositeToSurfaceResponse(surfaceQuery.data) : undefined),
    [surfaceQuery.data],
  );
  const state = useMemo(() => campaignToTailorState(campaign), [campaign]);

  // Raw / compatible / corrected evidence (adbTailorSlice — the same trust audit the ADB explorer
  // uses): fit a local slice model over the campaign points, then classify each point. raw = its
  // solved value; compatible = raw where the local model transports it within tolerance; corrected
  // = raw + the model's correction (rejected points dropped). The toggle drives which points the
  // surface shows; the summary counts the whole set. Evidence mode rides the shared
  // Study Cursor so every ADB-derived card recolours together.
  const evidenceMode: TailorEvidenceMode = cursor.evidenceMode;
  const setEvidenceMode = (m: TailorEvidenceMode) => patchCursor({ evidenceMode: m as StudyEvidenceMode });
  const rows = useMemo(
    () => state.points.map((pt) => ({ ...pt.inputs, CL: pt.outputs?.CL ?? null })),
    [state.points],
  );
  const axisInputs = useMemo<TailorAxisInput[]>(
    () => (campaign.config?.axes ?? [])
      .filter((ax) => AXIS_LABELS.has(ax.axis))
      .map((ax) => {
        const lo = ax.mode === "range" ? ax.min ?? 0 : ax.value ?? 0;
        const hi = ax.mode === "range" ? ax.max ?? 0 : ax.value ?? 0;
        return { id: ax.axis, label: AXIS_LABELS.get(ax.axis)!, min: lo, max: hi, mean: (lo + hi) / 2, sample_count: rows.length };
      }),
    [campaign, rows.length],
  );
  const model = useMemo(() => fitTailorSliceModel(rows, axisInputs, "CL"), [rows, axisInputs]);
  const evidence = useMemo(
    () => summarizeTailorEvidence(rows, model, "CL", sliceOverrides, x?.canonical ?? "", y?.canonical ?? ""),
    [rows, model, sliceOverrides, x, y],
  );
  // The surface's samples, re-valued + filtered by the chosen mode: corrected shows the corrected
  // CL and drops rejected points, compatible keeps only compatible points at their raw value, raw
  // shows every solved point. So the surface's points literally differ per mode (the "difference").
  const modePoints = useMemo(
    () => state.points.flatMap((pt, i) => {
      const v = tailorValueForMode(evidenceMode, rows[i]!, model, "CL", sliceOverrides, x?.canonical ?? "", y?.canonical ?? "");
      if (v == null) return [];
      return [{ ...pt, outputs: { CL: v } }];
    }),
    [state.points, rows, model, evidenceMode, sliceOverrides, x, y],
  );

  if (!x || !y || axes.length < 2) return null;

  const chip = (label: string, active: boolean, onClick: () => void, key: string) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      style={{
        fontSize: 9.5, fontFamily: MONO, padding: "2px 7px", borderRadius: 999, cursor: "pointer",
        border: `1px solid ${active ? palette.primary : palette.line}`,
        background: active ? palette.primarySoft : palette.surface2,
        color: active ? palette.primary : palette.ink3,
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="fill-tailor-surface">
      {/* Axis picker only when there is a genuine choice (>2 swept axes). Picking x moves y off
          it automatically; with exactly 2 swept axes there is nothing to choose, so no controls. */}
      {pickable.length > 2 ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 9.5, fontFamily: MONO, color: palette.ink4 }}>view</span>
          {pickable.map((a) => chip(
            a.label,
            a.canonical === x.canonical || a.canonical === y?.canonical,
            () => {
              if (a.canonical === x.canonical || a.canonical === y?.canonical) return;
              // With a single swept axis the picker falls back to ALL axes (a 2D view needs a
              // second axis), but the swept axis must never be evicted from BOTH slots — all the
              // data varies along it, so a view without it is a degenerate constant surface.
              const locked = ranged.length === 1 ? ranged[0]! : null;
              if (locked && a.canonical !== locked.canonical) {
                setXY(a.canonical, locked.canonical);
              } else {
                setXY(a.canonical, x.canonical);
              }
            },
            `ax-${a.canonical}`,
          ))}
        </div>
      ) : null}

      {/* Raw / compatible / corrected evidence toggle (adbTailorSlice); the count only elaborates
          when the modes actually differ (flagged/rejected points exist). */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 9.5, fontFamily: MONO, color: palette.ink4 }}>points</span>
        {(["raw", "compatible", "corrected"] as const).map((m) =>
          chip(m, evidenceMode === m, () => setEvidenceMode(m), `ev-${m}`))}
        <span style={{ marginLeft: "auto", fontSize: 9.5, fontFamily: MONO, color: palette.ink3 }}>
          {evidence.correctedFlagged > 0 || evidence.correctedRejected > 0
            ? `${evidence.raw} pts · ${evidence.correctedFlagged} flagged · ${evidence.correctedRejected} rejected`
            : `${evidence.raw} pts`}
        </span>
      </div>

      {/* Held axes fold away — a one-line summary, sliders on demand. */}
      {pinAxes.length > 0 ? (
        <details style={{ fontSize: 9.5, fontFamily: MONO, color: palette.ink3 }}>
          <summary style={{ cursor: "pointer", color: palette.ink4 }}>
            held: {pinAxes.map((a) => `${a.label} ${a.fmt(sliceOverrides[a.canonical] ?? 0)}`).join(" · ")}
          </summary>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingTop: 6 }}>
            {pinAxes.map((a) => (
              <label key={a.canonical} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 118, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {a.label} @ {a.fmt(sliceOverrides[a.canonical] ?? 0)}
                </span>
                <input
                  type="range" min={a.min} max={a.max} step={(a.max - a.min) / 100}
                  value={sliceOverrides[a.canonical] ?? (a.min + a.max) / 2}
                  onChange={(e) => setPin(a.canonical, Number(e.target.value))}
                  style={{ flex: 1 }}
                />
              </label>
            ))}
          </div>
        </details>
      ) : null}
      {/* 3D: the composite surface, re-fitting as the fill lands points (surfaceOverride = real
          data, so the adaptive-sampling mock is never touched). campaignId undefined disables the
          framework's own surface fetch. */}
      <TailorSurfaceBuild
        campaignId={undefined}
        state={state}
        points={modePoints}
        selectedOutput="CL"
        focusXAxis={x.canonical}
        focusYAxis={y.canonical}
        methodLabel="Tailor fill"
        sampleLabel="fill"
        sliceOverrides={sliceOverrides}
        extrapolationMode="extrap"
        surfaceOverride={adapted}
        // Shared 3D camera (§5.4 view3d): this lane orbits in lockstep with
        // every other 3D lane and the pose survives demotion + reloads
        // (review fix: view3d was persisted but consumed by nothing).
        viewState={cursor.view3d}
        onViewStateChange={(v) => patchCursor({ view3d: v })}
      />

      {/* 2D slice: the estimate heatmap + uncertainty + ridge, the top-down companion to the 3D. */}
      {adapted ? (
        <div style={{ height: 220 }}>
          <HeatmapPanel
            x={adapted.x}
            y={adapted.y}
            z={adapted.mean}
            uncertainty={adapted.sigma}
            xLabel={x.label}
            yLabel={y.label}
            zLabel="CL"
            highlight="ridge"
          />
        </div>
      ) : (
        <div style={{ fontSize: 11, fontFamily: MONO, color: palette.ink4 }}>
          {surfaceQuery.isError ? "surface unavailable — solve a few points" : "fitting the fill surface…"}
        </div>
      )}
    </div>
  );
}
